import { useEffect, useState } from 'react';

/**
 * Conversa do painel com a extensão do Lictor (Etapa 07, botão "Abrir já logado").
 *
 * O painel não fala com a extensão diretamente: manda `window.postMessage` para a própria janela,
 * e o content script da extensão (que roda nesta página) escuta e responde. Mensagens:
 * - `LICTOR_PING` → a extensão responde `LICTOR_PONG` com a versão. Sem resposta em 500 ms, a
 *   extensão não está instalada (ou é antiga, sem o "Abrir"). D23: aí o "Acessar" só abre a página
 *   de login numa aba nova e o cartão mostra o aviso para instalar (ver acessar.ts);
 * - `LICTOR_OPEN` com o id da credencial → a extensão abre o tribunal e faz o login. Leva SÓ o id:
 *   quem decide se o usuário pode usar a credencial é o backend.
 * - D16b: depois do `LICTOR_OPEN`, a extensão responde `LICTOR_OPEN_RESULT` com `{credentialId, ok, erro?}`.
 *   Quando falha, o painel mostra o `erro` (o mesmo texto que o popup da extensão mostra).
 *
 * Nada aqui envolve login, senha ou código.
 */
export const LICTOR_PING = 'LICTOR_PING';
export const LICTOR_PONG = 'LICTOR_PONG';
export const LICTOR_OPEN = 'LICTOR_OPEN';
export const LICTOR_OPEN_RESULT = 'LICTOR_OPEN_RESULT';
export const PRAZO_DO_PING_MS = 500;
/** D16b: quanto esperar a resposta do "Abrir" (o service worker consulta o backend antes de abrir a aba). */
export const PRAZO_DA_ABERTURA_MS = 20_000;
/** D16b: texto que a extensão já usa quando a aba não pôde ser aberta; serve para resposta sem texto. */
export const ERRO_AO_ABRIR = 'Não foi possível abrir a aba';
/**
 * Sem resposta dentro do prazo (quase sempre o tribunal demorando). Texto aprovado pelo Léo em
 * 02/11/2026 (antes: "A extensão do Lictor não respondeu. Tente abrir de novo.").
 */
export const ERRO_SEM_RESPOSTA_DA_EXTENSAO =
  'O tribunal demorou para responder. Tente de novo em alguns minutos ou entre direto pelo site do tribunal.';

export type BotaoAbrir = 'abrir' | 'instalar';

/** "Abrir" quando a extensão respondeu o ping (com a versão); senão, "Instalar extensão". */
export function decidirBotaoAbrir(versaoDaExtensao: string | null | undefined): BotaoAbrir {
  return typeof versaoDaExtensao === 'string' && versaoDaExtensao.length > 0 ? 'abrir' : 'instalar';
}

/** Versão da extensão se a mensagem for um LICTOR_PONG vindo desta mesma janela; senão, null. */
export function versaoDoPong(
  evento: Pick<MessageEvent, 'source' | 'origin' | 'data'>,
  janela: Pick<Window, 'location'>
): string | null {
  if (evento.source !== janela || evento.origin !== janela.location.origin) return null;
  const dados = evento.data;
  if (typeof dados !== 'object' || dados === null || dados.tipo !== LICTOR_PONG) return null;
  return typeof dados.versao === 'string' && dados.versao.length > 0 && dados.versao.length <= 20 ? dados.versao : null;
}

type JanelaDoPainel = Pick<Window, 'location' | 'postMessage' | 'addEventListener' | 'removeEventListener'>;

/** Avisa a cada LICTOR_PONG recebido (inclusive o que a extensão manda sozinha ao carregar). */
export function observarExtensao(aoResponder: (versao: string) => void, janela: JanelaDoPainel = window): () => void {
  const ouvinte = (evento: MessageEvent) => {
    const versao = versaoDoPong(evento, janela);
    if (versao) aoResponder(versao);
  };
  janela.addEventListener('message', ouvinte);
  return () => janela.removeEventListener('message', ouvinte);
}

/** Pergunta à extensão se ela está aí. Resolve com a versão, ou null depois do prazo. */
export function perguntarAExtensao(janela: JanelaDoPainel = window, prazoMs = PRAZO_DO_PING_MS): Promise<string | null> {
  return new Promise((resolve) => {
    let parar = () => {};
    const prazo = setTimeout(() => {
      parar();
      resolve(null);
    }, prazoMs);
    parar = observarExtensao((versao) => {
      clearTimeout(prazo);
      parar();
      resolve(versao);
    }, janela);
    janela.postMessage({ tipo: LICTOR_PING }, janela.location.origin);
  });
}

export type ResultadoDaAbertura = { ok: true } | { ok: false; erro: string };

/**
 * D16b: o resultado do "Abrir" desta credencial, se a mensagem for um LICTOR_OPEN_RESULT vindo desta
 * mesma janela e da mesma origem (as mesmas checagens do PONG); senão, null.
 */
export function resultadoDaAbertura(
  evento: Pick<MessageEvent, 'source' | 'origin' | 'data'>,
  janela: Pick<Window, 'location'>,
  credentialId: string
): ResultadoDaAbertura | null {
  if (evento.source !== janela || evento.origin !== janela.location.origin) return null;
  const dados = evento.data;
  if (typeof dados !== 'object' || dados === null || dados.tipo !== LICTOR_OPEN_RESULT) return null;
  if (dados.credentialId !== credentialId) return null;
  if (dados.ok === true) return { ok: true };
  if (dados.ok !== false) return null;
  const erro = typeof dados.erro === 'string' && dados.erro.trim() && dados.erro.length <= 200 ? dados.erro : ERRO_AO_ABRIR;
  return { ok: false, erro };
}

/**
 * Pede à extensão para abrir o tribunal da credencial já logado. Só depois de um clique de verdade.
 * D16b: resolve com o resultado que a extensão devolver; sem resposta no prazo, resolve como falha
 * (o painel nunca fica esperando para sempre).
 */
export function pedirParaAbrir(
  credentialId: string,
  janela: JanelaDoPainel = window,
  prazoMs = PRAZO_DA_ABERTURA_MS
): Promise<ResultadoDaAbertura> {
  return new Promise((resolve) => {
    const ouvinte = (evento: MessageEvent) => {
      const resultado = resultadoDaAbertura(evento, janela, credentialId);
      if (!resultado) return;
      clearTimeout(prazo);
      janela.removeEventListener('message', ouvinte);
      resolve(resultado);
    };
    const prazo = setTimeout(() => {
      janela.removeEventListener('message', ouvinte);
      resolve({ ok: false, erro: ERRO_SEM_RESPOSTA_DA_EXTENSAO });
    }, prazoMs);
    janela.addEventListener('message', ouvinte);
    janela.postMessage({ tipo: LICTOR_OPEN, credentialId }, janela.location.origin);
  });
}

/** D16b: clique em "Abrir" no cartão ou na linha: pede à extensão e, se falhar, mostra o erro (alerta). */
export async function abrirEMostrarErro(
  credentialId: string,
  janela: JanelaDoPainel = window,
  mostrarErro: (texto: string) => void = (texto) => alert(texto)
): Promise<void> {
  const resultado = await pedirParaAbrir(credentialId, janela);
  if (!resultado.ok) mostrarErro(resultado.erro);
}

// Uma pergunta só por página, por mais cartões que existam.
let versaoConhecida: string | null = null;
let perguntaEmAndamento: Promise<string | null> | null = null;

function consultarExtensao(): Promise<string | null> {
  if (versaoConhecida) return Promise.resolve(versaoConhecida);
  if (!perguntaEmAndamento) {
    perguntaEmAndamento = perguntarAExtensao().then((versao) => {
      perguntaEmAndamento = null; // sem resposta, a próxima tela pergunta de novo
      if (versao) versaoConhecida = versao;
      return versao;
    });
  }
  return perguntaEmAndamento;
}

/**
 * Se a extensão respondeu: "abrir" (sim), "instalar" (não), ou null enquanto ainda não se sabe
 * (no máximo 500 ms) ou quando a credencial não tem endereço para o "Acessar" (`ativo` falso).
 */
export function useBotaoAbrir(ativo: boolean): BotaoAbrir | null {
  const [versao, setVersao] = useState<string | null | undefined>(ativo && versaoConhecida ? versaoConhecida : undefined);

  useEffect(() => {
    if (!ativo) return;
    let vivo = true;
    consultarExtensao().then((resposta) => {
      if (vivo) setVersao((atual) => atual || resposta);
    });
    // A extensão pode responder depois (ex.: carregou depois do painel): o botão passa a "Abrir".
    const parar = observarExtensao((resposta) => {
      versaoConhecida = resposta;
      if (vivo) setVersao(resposta);
    });
    return () => {
      vivo = false;
      parar();
    };
  }, [ativo]);

  if (!ativo || versao === undefined) return null;
  return decidirBotaoAbrir(versao);
}
